/**
 * How many SMS a message actually is.
 *
 * This is the number on the confirmation screen and the number the provider
 * bills, and the specification's "160 or 70" is right but incomplete in three
 * ways that all move the answer (PLAN §6):
 *
 *   1. Nine characters — `^ { } \ [ ] ~ | €` — are not in the GSM alphabet at
 *      all. They are sent as an escape *plus* a character and cost **two**.
 *   2. A concatenated message loses six characters per part to the header:
 *      153 rather than 160, 67 rather than 70.
 *   3. Neither an escape pair nor a surrogate pair may be split across parts,
 *      so a message can need another part while still being "short enough".
 *
 * Pure by construction (PLAN §4): the same code runs in the browser as the
 * administrator types and on the server before anything is sent, so the count
 * they approved is provably the count that goes out.
 */

export type SmsEncoding = 'gsm-7' | 'ucs-2';

/** A single GSM-7 message; above this the header cost applies to every part. */
export const GSM7_SINGLE = 160;
export const GSM7_MULTI = 153;

/** UCS-2 is measured in UTF-16 code units, so an emoji is two of them. */
export const UCS2_SINGLE = 70;
export const UCS2_MULTI = 67;

export interface SmsMetrics {
  readonly encoding: SmsEncoding;
  /** Length as the provider counts it: an escape pair is two, an emoji is two. */
  readonly length: number;
  /** How many SMS this becomes. Zero for an empty message — nothing is sent. */
  readonly parts: number;
  /** Total room at this many parts. */
  readonly capacity: number;
  /** Characters left before another part is needed. Never negative. */
  readonly remaining: number;
}

/**
 * The GSM 03.38 basic table (3GPP TS 23.038), in order, minus the escape at
 * 0x1B. Anything outside it and the extension table below forces UCS-2 — which
 * is why any Cyrillic at all drops the message to 70 characters.
 */
const BASIC =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?' +
  '¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';

/** Sent as escape + character, and therefore two characters wide. */
const EXTENDED = '\f^{}\\[~]|€';

const BASIC_SET = new Set(BASIC);
const EXTENDED_SET = new Set(EXTENDED);

/** True when every character can be sent in the GSM alphabet. */
export function isGsm7(text: string): boolean {
  for (const character of text) {
    if (!BASIC_SET.has(character) && !EXTENDED_SET.has(character)) return false;
  }
  return true;
}

/**
 * Measures `text` exactly as written. Nothing is normalised here on purpose: a
 * decomposed `й` really is two UTF-16 code units, and reporting it as one would
 * be a count of a message we are not sending.
 */
export function measureSms(text: string): SmsMetrics {
  const gsm = isGsm7(text);
  const widths = gsm ? gsmWidths(text) : ucs2Widths(text);
  const length = widths.reduce((total, width) => total + width, 0);

  const single = gsm ? GSM7_SINGLE : UCS2_SINGLE;
  const multi = gsm ? GSM7_MULTI : UCS2_MULTI;
  const encoding: SmsEncoding = gsm ? 'gsm-7' : 'ucs-2';

  if (length === 0) {
    // An empty draft is not one empty SMS; it is nothing to send yet.
    return { encoding, length: 0, parts: 0, capacity: single, remaining: single };
  }

  const parts = length <= single ? 1 : pack(widths, multi);
  const capacity = parts === 1 ? single : parts * multi;

  return { encoding, length, parts, capacity, remaining: Math.max(0, capacity - length) };
}

/** Convenience for the places that only need the number. */
export function countSegments(text: string): number {
  return measureSms(text).parts;
}

function gsmWidths(text: string): readonly number[] {
  // Iterating by code point is safe here: everything outside the BMP has
  // already forced UCS-2 by the time this runs.
  return [...text].map((character) => (EXTENDED_SET.has(character) ? 2 : 1));
}

function ucs2Widths(text: string): readonly number[] {
  // A surrogate pair is one character of two code units, and the two halves
  // cannot be separated — hence a width rather than a count.
  return [...text].map((character) => character.length);
}

/**
 * Fills parts of `size`, never splitting a two-wide character across the join.
 *
 * The straightforward `ceil(length / size)` is wrong exactly when a two-wide
 * character lands on a boundary: the pair moves whole into the next part and
 * the one before it is sent a character short. Rare, and precisely the case
 * where an estimate that was one part low would surprise someone with a bill.
 */
function pack(widths: readonly number[], size: number): number {
  let parts = 1;
  let used = 0;

  for (const width of widths) {
    if (used + width > size) {
      parts += 1;
      used = 0;
    }
    used += width;
  }
  return parts;
}
